<template>
  <div>
    <Toast />
    <HeaderBreadcrumbs
      :items="[
        { label: 'Publisher', to: '/vendor' },
        { label: 'Edit' }
      ]"
      title="Edit Publisher"
    />

    <div class="flex items-center px-4 py-3 my-3 space-x-4 bg-white rounded-lg shadow-sm tab-header">
      <div :class="['tab-item flex items-center', { 'text-blue-500 font-semibold': currentTab === 1 }]">
        <span :class="['font-bold rounded-full border p-1.5 mr-2 text-center w-9 h-9', { 'border-custom': currentTab === 1 }]">1</span>
        Publisher Details
      </div>
      <span class="text-gray-400">»</span>
      <div :class="['tab-item flex items-center', { 'text-blue-500 font-semibold': currentTab === 2 }]">
        <span :class="['font-bold rounded-full border p-1.5 mr-2 text-center w-9 h-9', { 'border-custom': currentTab === 2 }]">2</span>
        Publisher Agreement
      </div>
      <span class="text-gray-400">»</span>
      <div :class="['tab-item flex items-center', { 'text-blue-500 font-semibold': currentTab === 3 }]">
        <span :class="['font-bold rounded-full border p-1.5 mr-2 text-center w-9 h-9', { 'border-custom': currentTab === 3 }]">3</span>
        Publisher Report Format
      </div>
    </div>

    <div class="flex-col w-full mb-3 bg-white rounded-lg">

      <div v-if="currentTab === 1" class="flex items-center justify-between w-full px-4 py-3 mt-3 rounded-lg ">
        <div class="flex flex-col w-1/2">
          <FormLabel htmlFor="vendor-name">
            Publisher Name
          </FormLabel>

          <TextInput
            type="text"
            id="vendor-name"
            placeholder="Enter Vendor Name"
            :variant="isSubmitted && isNameError ? 'error' : 'default'"
            v-model="form.name"
          />

          <div v-if="isSubmitted && isNameError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="vendor-name" className="mt-3">
            Publisher Image (Optional)
          </FormLabel>

          <div>
            <label for="input-image" class="rounded-lg cursor-pointer">
              <img
                v-if="form.preview"
                :src="form.preview"
                class="object-contain rounded-lg img-fluid"
                style="height: 144px; width: 144px;"
                height="144"
                width="144"
              />
              <div
                v-else
                :class="['flex flex-col items-center bg-gray-50 border-2 px-1 border-gray-200 hover:border-gray-400 border-dashed rounded-lg gap-2']"
                style="height: 144px; width: 144px; padding-top: 37px;"
              >
                <FileCirlclePlusFilled />
                <div class="text-xs font-semibold text-gray-500">
                  Upload Image
                </div>
                <div class="text-xs font-normal text-gray-500">
                  Max 2MB
                </div>
              </div>
            </label>
          </div>

          <input
            id="input-image"
            type="file"
            accept="image/*"
            class="hidden"
            @change="previewImage"
          />

          <FormLabel htmlFor="organization" className="mt-3">
            Organization
          </FormLabel>

          <multiselect
            v-model="form.organization"
            label="name"
            track-by="name"
            placeholder="Select Organization"
            open-direction="bottom"
            class="box-border custom-multiselect"
            :class="isSubmitted && isOrganizationError ? 'error' : ''"
            :options="organizationOptions"
            :searchable="true"
            :loading="isLoadingOrganization"
            :close-on-select="true"
            @search-change="handleSearchOrganization"
          >
            <span slot="noResult">
              Oops! No elements found. Consider changing the search query.
            </span>
          </multiselect>

          <div v-if="isSubmitted && isOrganizationError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="isExternal" className="mt-3">
            Publisher Type
          </FormLabel>

          <SelectRadio
            id="isExternal"
            defaultText="Select Publisher Type"
            v-model="form.isExternal"
            :options="vendorTypeOptions"
            :isError="isSubmitted && isExternalError"
          />

          <div v-if="isSubmitted && isExternalError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="vendorSource" className="mt-3">
            Vendor Source
          </FormLabel>

          <SelectRadio
            id="vendorSource"
            defaultText="Select Publisher Source"
            v-model="form.vendorSource"
            :options="vendorSourceOptions"
            :isError="isSubmitted && isVendorSourceError"
          />

          <div v-if="isSubmitted && isVendorSourceError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="vendorSource" className="mt-3">
            Publisher Content (Optional)
          </FormLabel>

          <SelectCheckbox
            id="vendorContent"
            defaultText="Select Publisher Content"
            v-model="form.contentTypes"
            :options="contentTypeOptions"
          />

          <FormLabel htmlFor="status" className="mt-3">
            Approval Status
          </FormLabel>

          <SelectRadio
            id="status"
            defaultText="Select Approval Status"
            v-model="form.status"
            :options="statusOptions"
            :isError="isSubmitted && isStatusError"
          />

          <div v-if="isSubmitted && isStatusError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="vendorId" className="mt-3">
            Vendor ID from AX
          </FormLabel>

          <TextInput
            type="text"
            id="vendorId"
            placeholder="Enter Publisher ID from AX"
            :variant="isSubmitted && isVendorIdAxError ? 'error' : 'default'"
            v-model="form.vendorIdAx"
          />

          <div v-if="isSubmitted && isVendorIdAxError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="description" className="mt-3">
            Description (Optional)
          </FormLabel>

          <TextArea
            id="description"
            rows="7"
            placeholder="Enter something..."
            v-model="form.description"
          />

          <FormLabel htmlFor="status" className="mt-3">
            Status
          </FormLabel>

          <GmSwitch id="status" v-model="form.isActive"/>

          <div class="flex gap-5 mt-2">
            <Button @click="validateAndNext">
              Next
            </Button>

            <Button
              variant="outlined"
              color="alternative"
              @click="prevTab"
            >
              Cancel
            </Button>
          </div>

        </div>

      </div>
      <div v-if="currentTab === 2" class="flex items-center justify-between w-full px-4 py-3 mt-3 rounded-lg">
        <div class="w-full">
          <Table
            :headers="vATableHeaders"
            :data="itemRevenuesV2 ?? []"
            :rowKey="vaTableRowKeys"
            :currentPage="vACurrentPage"
            :perPage="vATablePerPage"
            :isLoading="vAIsLoading"
            :withNumberCol="false"
            :withTopRow="true"
            className="w-full"
          >
            <template #top-row>
              <td class="p-2 text-left create-cell">
                <Button
                  variant="outlined"
                  color="primary"
                  size="xs"
                  className="w-full"
                  @click="createVendorAgreement"
                >
                  <div class="flex items-center justify-center">
                    <PlusOutline className="w-3 h-3 mr-1 text-blue-700" />
                    Add New
                  </div>
                </Button>
              </td>
            </template>

            <template #default_percentage_amount="{ value }">
              {{ value * 100 }}%
            </template>

            <template #valid_from="{ value }">
              <div>
                {{ formatAgreementDate(value) }}
              </div>
              <div class="text-xs italic text-gray-500">
                {{ formatAgreementTime(value) }}
              </div>
            </template>

            <template #valid_to="{ value }">
              <div>
                {{ formatAgreementDate(value) }}
              </div>
              <div class="text-xs italic text-gray-500">
                {{ formatAgreementTime(value) }}
              </div>
            </template>

            <template #is_active="{ value }">
              <div>
                <Label
                  :variant="value ? 'green' : 'gray'"
                >
                  {{ value ? 'Active' : 'Inactive' }}
                </Label>
              </div>
            </template>

            <template #actions="{ row }">
              <router-link
                :to="{ path: `/vendor/edit/${row.vendor_id}/agreement/edit/${row.id}` }"
                title="Edit"
                class="inline-block mx-auto"
              >
                <EditFilled />
              </router-link>
            </template>
          </Table>

          <div class="flex justify-between pt-4 pl-3 pr-3 mt-1 mb-2 bg-white border-t border-gray-100" v-if="!!Number(itemRevenuesV2TotalRows)">
            <div class="font-semibold">
              <span class="font-normal text-gray-500">Showing </span> {{ vACurrentPage }}-{{ Number(itemRevenuesV2TotalRows) >= vATablePerPage ? vATablePerPage : Number(itemRevenuesV2TotalRows) }} <span
                class="font-normal text-gray-500">of</span> {{ parseInt(itemRevenuesV2TotalRows) }}
            </div>
            <div class="mb-2">
              <t-pagination :disabled="vAIsLoading" :total-items="Number(itemRevenuesV2TotalRows)" :per-page="vATablePerPage" v-model="vACurrentPage"
                @change="vAOnClickPage" />
            </div>
          </div>

          <div class="flex gap-5 mt-2">
            <Button @click="validateAndNext">
              Next
            </Button>

            <Button
              variant="outlined"
              color="alternative"
              @click="prevTab"
            >
              Back
            </Button>
          </div>

        </div>

      </div>

      <div v-if="currentTab === 3" class="flex items-center justify-between w-full px-4 py-3 mt-3 rounded-lg ">
        <div class="flex flex-col w-1/2">

          <FormLabel htmlFor="contentType">
            Content Type
          </FormLabel>

          <div class="flex gap-6">
            <b-form-checkbox v-model="form.vRFIsSingle" :unchecked-value="false">
              Single
            </b-form-checkbox>

            <b-form-checkbox v-model="form.vRFIsBundle" :unchecked-value="false">
              Bundle
            </b-form-checkbox>

            <b-form-checkbox v-model="form.vRFIsSubscription" :unchecked-value="false">
              Subscription
            </b-form-checkbox>

            <b-form-checkbox v-model="form.vRFIsBuffet" :unchecked-value="false">
              Buffet
            </b-form-checkbox>
          </div>

          <FormLabel htmlFor="reason" class="mt-3">
            Reason (Optional)
          </FormLabel>

          <TextArea
            id="reason"
            rows="7"
            placeholder="Type reason here..."
            v-model="form.vRFReason"
          />

          <FormLabel htmlFor="status" class="mt-3">
            Status
          </FormLabel>

          <GmSwitch id="status" v-model="form.vRFIsActive"/>

          <div class="flex gap-5 mt-4">
            <Button @click="updateVendor">
              Save
            </Button>

            <Button
              variant="outlined"
              color="alternative"
              @click="prevTab"
            >
              Back
            </Button>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import moment from 'moment';
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';
import FormLabel from '@/components/ui/FormLabel';
import GmSwitch from '@/components/ui/Switch';
import TextInput from '@/components/ui/TextInput';
import TextArea from '@/components/ui/TextArea';
import Button from '@/components/ui/Button';
import SelectRadio from '@/components/ui/SelectRadio';
import SelectCheckbox from '@/components/ui/SelectCheckbox';
import Label from '@/components/ui/Label';
import EditFilled from '@/components/icons/EditFilled';
import PlusOutline from '@/components/icons/PlusOutline';
import FileCirlclePlusFilled from '@/components/icons/FileCirclePlusFilled';
import Table from '@/components/form/table';

import API from '@/plugins/http';
import Toast from '@/components/form/Toast';
import { EventBus } from '@/event-bus';

import constant from '@/store/constant';

const AGREEMENT_PAGE_SIZE = 10;

const paymentCurrencies = Object.entries(constant.PUBLISHER_PAYMENT_CURRENCY).map(
  (val) => ({
    text: val[0],
    value: val[1],
  }),
);

export default {
  name: 'vendorNewEdit',
  components: {
    HeaderBreadcrumbs,
    FormLabel,
    GmSwitch,
    TextInput,
    TextArea,
    Button,
    SelectRadio,
    SelectCheckbox,
    Label,
    FileCirlclePlusFilled,
    Table,
    EditFilled,
    PlusOutline,
    Toast
  },
  data() {
    return {
      currentTab: 1,
      form: {
        name: '',
        preview: '',
        image: '',
        organization: null,
        status: null,
        isExternal: null,
        vendorSource: null,
        vendorIdAx: '',
        contentTypes: [],
        description: '',
        isActive: false,

        vACurrency: null,
        vARevenue: null,
        vAStartDate: moment().toString(),
        vAEndDate: moment().toString(),
        vAIsActive: false,

        vRFIsActive: false,
        vRFReason: '',
        vRFIsSingle: false,
        vRFIsBundle: false,
        vRFIsSubscription: false,
        vRFIsBuffet: false,
      },

      organizationOptions: [],
      isLoadingOrganization: false,

      contentTypeOptions: [
        { value: 'isContentBooks', text: 'Books' },
        { value: 'isContentMagazines', text: 'Magazines' },
        { value: 'isContentNewspapers', text: 'Newspapers' }
      ],

      vendorTypeOptions: [
        { value: false, text: 'Internal' },
        { value: true, text: 'External' }
      ],
      vendorSourceOptions: [
        { value: 'Gramedia Digital', text: 'Gramedia Digital' },
        { value: 'SmartLib Client', text: 'SmartLib Client', },
      ],
      statusOptions: [
        { value: 1, text: 'New' },
        {
          value: 2,
          text: 'Waiting for Review',
        },
        { value: 3, text: 'In Review' },
        { value: 4, text: 'Rejected' },
        { value: 5, text: 'Approved' },
        { value: 6, text: 'Clear' },
        { value: 7, text: 'Banned' },
      ],

      currencyOptions: [
        { value: null, text: 'Select Payment Currency' },
        ...paymentCurrencies,
      ],

      isLoadingSearchRevenue: false,
      revenueItems: [],

      defaultFixed: null,
      defaultPercentage: null,
      isLoadingUpdateVendor: false,
      isSubmitted: false,

      vATableHeaders: [
        { label: 'No', key: 'no' },
        { label: 'Formula Name', key: 'formula_name' },
        { label: 'Default Fixed', key: 'default_fixed', class: 'w-1/12' },
        { label: 'Default Percentage', key: 'default_percentage', class: 'w-1/12' },
        { label: 'Start Date', key: 'start_date' },
        { label: 'End Date', key: 'end_date' },
        { label: 'Status', key: 'status', class: 'w-1/12' },
      ],

      vaTableRowKeys: [
        'formula_name',
        'default_fixed_amount',
        'default_percentage_amount',
        'valid_from',
        'valid_to',
        'is_active'
      ],

      vATablePerPage: AGREEMENT_PAGE_SIZE,
      vACurrentPage: 1,
      vAIsLoading: false,

      agreements: []

    }
  },
  computed: {
    ...mapState({
      itemsRevenueShares: ({ revenueshares }) => revenueshares.items,

      item: ({ vendors }) => vendors.item,
      itemRevenue: ({ vendors }) => vendors.itemRevenue,
      isLoadingItemRevenue: ({ vendors }) => vendors.isLoadingItemRevenue,
      isLoadingVendor: ({ vendors }) => vendors.isLoading,
      itemRevenuesV2: ({ vendors }) => vendors.itemRevenuesV2,
      itemRevenuesV2TotalRows: ({ vendors }) => vendors.itemRevenuesV2TotalRows,
      isLoadingItemRevenuesV2: ({ vendors }) => vendors.isLoadingItemRevenuesV2,
      itemVendor: ({ vendorScoopint }) => vendorScoopint.item,
      content: ({ dashboard }) => dashboard.Content,
      isLoadingContent: ({ dashboard }) => dashboard.isLoading,
      vendorReportFormat: ({ vendorReportFormat }) => vendorReportFormat.item,
      isLoadingVendorReportFormat: ({ vendorReportFormat }) => vendorReportFormat.isLoading,

      itemsVendorRevenue: ({ vendor_revenue }) => vendor_revenue.items,

    }),
    isNameError() {
      return !this.form.name.trim();
    },
    isOrganizationError() {
      return !this.form.organization;
    },
    isExternalError() {
      return this.form.isExternal === null;
    },
    isVendorSourceError() {
      return !this.form.vendorSource;
    },
    isStatusError() {
      return !this.form.status;
    },
    isVendorIdAxError() {
      return !this.form.vendorIdAx || !this.form.vendorIdAx.trim();
    },
    isVACurrencyError() {
      return !this.form.vACurrency;
    },
    isVARevenueError() {
      return !this.form.vARevenue;
    },
    isVAStartDateError() {
      return  !this.form.vAStartDate;
    },
    isVAEndDateError() {
      return !this.form.vAEndDate;
    },
    vendorContent() {
      const content = [];

      if (this.item?.isContentBooks) {
        content.push('isContentBooks')
      }

      if (this.item?.isContentMagazines) {
        content.push('isContentMagazines')
      }

      if (this.item?.isContentNewspapers) {
        content.push('isContentNewspapers')
      }

      return content;
    },
  },
  mounted() {
    const vendorId = this.$route.params.id;
    this.fetchVendorsById({ id: vendorId });
    this.getVendorRevenue({ id: vendorId });
    this.fetchVendorFormatById({ id: vendorId });
    this.findVendorScoopintByID({ id: vendorId });
    this.fetchVendorAggrementById({ id: vendorId });
    this.getVendorRevenuesV2({ id: vendorId, page: 1, limit: AGREEMENT_PAGE_SIZE })

    // Handle tab query parameter for redirect from agreement pages (one-time only)
    if (this.$route.query.tab === '2') {
      this.currentTab = 2;
      // Remove the tab query parameter to prevent it from affecting future refreshes
      this.$router.replace({
        path: this.$route.path,
        query: { ...this.$route.query, tab: undefined }
      });
    }
  },
  methods: {
    ...mapActions('organization', ['searchOrganization']),
    ...mapActions('vendor_revenue', [
      'findVendorRevenueByID',
      'findVendorRevenue',
      'fetchVendorAggrementById'
    ]),

    ...mapActions('vendors', [
      'fetchVendorsById',
      'getVendorRevenue',
      'getVendorRevenuesV2'
    ]),
    ...mapActions('vendorScoopint', ['findVendorScoopintByID']),
    ...mapActions('vendorReportFormat', ['fetchVendorFormatById']),

    previewImage(event) {
      const input = event.target;
      if (input.files) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.form.preview = e.target.result;
        }
        this.form.image = input.files[0];
        reader.readAsDataURL(input.files[0])
      }
    },
    handleSearchOrganization(keyword) {
      if (keyword.length > 2) {
        this.isLoadingOrganization = true;
        const payload = {
          search: keyword
        }
        this.searchOrganization(payload)
          .then((resp) => {
            this.organizationOptions = resp.data.data;
            this.isLoadingOrganization = false;
          })
          .catch((err) => {
            this.isLoadingOrganization = false;
            console.error(err)
          })
      }
    },
    validateAndNext() {
      if (this.currentTab >= 3) {
        return
      }

      if (this.currentTab === 1) {
        if (
          !this.isNameError &&
          !this.isOrganizationError &&
          !this.isExternalError &&
          !this.isVendorSourceError &&
          !this.isStatusError &&
          !this.isVendorIdAxError
        ) {
          this.isSubmitted = false;
          this.currentTab++;
        } else {
          this.isSubmitted = true;
        }
      } else if (this.currentTab === 2) {
        this.isSubmitted = false;
        this.currentTab++;
      }
    },

    prevTab() {
      if (this.currentTab > 1) {
        this.currentTab--;
      } else {
        this.$router.replace('/vendor')
      }
    },

    findRevenue(keyword) {
      if (keyword.length > 2) {
        this.isLoadingSearchRevenue = true;
        const payload = {
          q: keyword,
        };
        this.findVendorRevenue(payload)
          .then((resp) => {
            this.revenueItems = resp.data.data;
            this.isLoadingSearchRevenue = false;
          })
          .catch((err) => {
            this.isLoadingSearchRevenue = false;
            console.log(err);
          });
      }
    },

    selectRevenue(data) {
      this.findVendorRevenueByID(data).then((res) => {
        const { default_fixed_amount, default_percentage_amount } = res.data.data;
        this.defaultFixed = default_fixed_amount;
        this.defaultPercentage = default_percentage_amount;
      });
    },

    async updateVendor() {
      this.isLoadingUpdateVendor = true;
      const formData = new FormData();
      formData.append('file', this.form.image);
      formData.append('vendor_details', JSON.stringify({
        name: this.form.name,
        organization: this.form.organization.id,
        status: this.form.status,
        vendor_id_ax: this.form.vendorIdAx,
        description: this.form.description,
        is_active: this.form.isActive,
        is_external: this.form.isExternal,
        is_content_books: this.form.contentTypes.includes('isContentBooks'),
        is_content_magazines: this.form.contentTypes.includes('isContentMagazines'),
        is_content_newspapers: this.form.contentTypes.includes('isContentNewspapers'),
        vendor_source: this.form.vendorSource
      }));
      formData.append('vendor_report_format', JSON.stringify({
        status_report_format: this.form.vRFReason,
        is_active_report_format: this.form.vRFIsActive,
        single: this.form.vRFIsSingle,
        subscription: this.form.vRFIsSubscription,
        bundle: this.form.vRFIsBundle,
        buffet: this.form.vRFIsBuffet
      }));

      try {
        await API.put(`/vendor/edit/v2/${this.$route.params.id}`, formData)
          .then((res) => {
            if (res.status === 200) {
              this.$router.push('/vendor');
              EventBus.$emit('show-toast', {
                message: 'Vendor has been updated.',
                type: 'success',
              });
            }
          })
      } catch (error) {
        console.error(error);
        EventBus.$emit('show-toast', {
          message: 'Failed To Update Vendor',
          type: 'error',
        });
      } finally {
        this.isLoadingCreateVendor = false;
      }
    },

    createVendorAgreement() {
      this.$router.push(`/vendor/edit/${this.$route.params.id}/agreement/create`)
    },

    setByItem() {
      this.form.name = this.item.name;
      this.form.preview = this.item.icon_image_normal ? `${constant.IMG_URL_STATIC_VENDOR.URL}${this.item.icon_image_normal}` : '';
      this.form.image = this.item.icon_image_normal;
      this.form.organization = this.item.cas_organization
      this.form.status = this.item.vendor_status;
      this.form.isExternal = this.item.is_external;
      this.form.vendorSource = this.item.vendor_source;
      this.form.vendorIdAx = this.item?.sync_vendor?.vendor_id_ax;
      this.form.contentTypes = this.vendorContent;
      this.form.description = this.item.description;
      this.form.isActive = this.item.is_active;
    },

    setByItemRevenue() {
      this.form.vACurrency = this.itemRevenue.payment_currency;
      this.form.vARevenue = this.itemRevenue.revenue_id;
      this.form.vAStartDate = null;
      this.form.vAEndDate = moment("2099-01-01").toString();
      this.form.vAIsActive = this.itemRevenue.is_active;
    },

    setByVendorReportFormat() {
      this.form.vRFIsActive = this.vendorReportFormat.is_active;
      this.form.vRFReason = this.vendorReportFormat.status;
      this.form.vRFIsSingle = this.vendorReportFormat.single;
      this.form.vRFIsBundle = this.vendorReportFormat.bundle;
      this.form.vRFIsSubscription = this.vendorReportFormat.subscription;
      this.form.vRFIsBuffet = this.vendorReportFormat.buffet;
    },
    formatAgreementDate(dateString) {
      return moment(dateString).format('DD MMM YYYY')
    },
    formatAgreementTime(dateString) {
      return moment(dateString).format('HH:mm:ss')
    },
    vAOnClickPage(page) {
      if (this.vAIsLoading) {
        return;
      }
      this.vACurrentPage = page;
      const vendorId = this.$route.params.id;
      this.getVendorRevenuesV2({ 
        id: vendorId, 
        page: page, 
        limit: this.vATablePerPage 
      });
    }
  },
  watch: {
    item(newValue) {
      if (!newValue || !Object.keys(this.item).length) {
        return;
      }

      this.setByItem();
    },
    itemRevenue(newValue) {
      if (!newValue || !Object.keys(this.itemRevenue).length) {
        return;
      }

      this.setByItemRevenue();
    },
    vendorReportFormat(newValue) {
      if (!newValue || !Object.keys(this.vendorReportFormat).length) {
        return;
      }

      this.setByVendorReportFormat();
    },
  }
}
</script>
